import React from 'react';
import SEO from './SEO';
import JsonLd from './JsonLd';
import Breadcrumbs from './Breadcrumbs';
import { FIRMA } from '../data/firma';
import { breadcrumbSchema, faqSchema } from '../data/schema';
import {
  AKTUALIZACJA,
  PPP_OLSZTYN,
  PUNKTY_POWIAT,
  ZA_ZYCIEM,
  PPP_WARMIA,
  PPP_ELBLAG,
  PPP_REGION,
  POMOC_SPOLECZNA,
  PORADNIE_FAQ,
} from '../data/poradnie';
import page from '../css/modules/ContentPage.module.css';

/**
 * Katalog placowek publicznych, do ktorych kierujemy rodzicow: poradnie
 * psychologiczno-pedagogiczne, program "Za zyciem" oraz pomoc spoleczna
 * (MOPS, GOPS, CUS, PCPR). Dane teleadresowe pochodza z src/data/poradnie.js.
 */

const PATH = '/poradnie-psychologiczne/';

const CRUMBS = [
  { name: 'Strona główna', path: '/' },
  { name: 'Poradnie psychologiczno-pedagogiczne', path: PATH },
];

const SPIS = [
  ['#kiedy', 'Poradnia czy gabinet SI'],
  ['#olsztyn', 'Poradnie w Olsztynie'],
  ['#powiat', 'Powiat olsztyński'],
  ['#za-zyciem', 'Program "Za życiem"'],
  ['#warmia', 'Warmia: Braniewo i sąsiednie powiaty'],
  ['#elblag', 'Elbląg i powiat elbląski'],
  ['#region', 'Pozostałe powiaty województwa'],
  ['#pomoc-spoleczna', 'MOPS, GOPS, CUS i PCPR'],
  ['#wizyta', 'Jak przygotować się do wizyty'],
  ['#faq', 'Najczęstsze pytania'],
];

/** Jedna karta placowki: nazwa, krotki opis i dane kontaktowe. */
function Placowka({ p, compact = false }) {
  return (
    <article className={compact ? page.miniCard : page.card}>
      <h3>{p.nazwa}</h3>
      {p.opis && <p className={page.muted}>{p.opis}</p>}
      <p style={{ marginTop: p.opis ? '0.7rem' : 0 }}>
        {p.adres}
        <br />
        {p.tel.map((t, i) => (
          <React.Fragment key={t}>
            {i > 0 && ', '}
            <a href={`tel:+48${t.replace(/\D/g, '')}`}>tel. {t}</a>
          </React.Fragment>
        ))}
        {p.email && (
          <>
            <br />
            <a href={`mailto:${p.email}`}>{p.email}</a>
          </>
        )}
        {p.www && (
          <>
            <br />
            <a href={p.wwwUrl} target="_blank" rel="noreferrer nofollow">
              {p.www}
            </a>
          </>
        )}
        {p.godziny && (
          <>
            <br />
            <span className={page.muted}>{p.godziny}</span>
          </>
        )}
      </p>
    </article>
  );
}

export default function Poradnie() {
  return (
    <div className={page.page}>
      <SEO
        title="Poradnie psychologiczno-pedagogiczne Olsztyn i Warmia - adresy, telefony, kontakt | Sensorini"
        description="Publiczne poradnie psychologiczno-pedagogiczne w Olsztynie, powiecie olsztyńskim, Braniewie, Elblągu i całym województwie warmińsko-mazurskim. Adresy, telefony, strony www, program Za życiem, MOPS i PCPR."
        pathname={PATH}
        type="article"
      />
      <JsonLd data={[breadcrumbSchema(CRUMBS), faqSchema(PORADNIE_FAQ)]} />

      <header className={page.hero}>
        <span className={`${page.blob} ${page.blobA}`} aria-hidden="true" />
        <span className={`${page.blob} ${page.blobB}`} aria-hidden="true" />
        <div className={page.heroInner}>
          <span className={page.eyebrow}>Polecane placówki • Warmia i Mazury</span>
          <h1 className={page.h1}>
            Poradnie psychologiczno-pedagogiczne, z którymi współpracujemy i które polecamy
          </h1>
          <p className={page.lead}>
            Adresy, telefony i strony poradni w Olsztynie, powiecie olsztyńskim, Braniewie, Elblągu i całym
            województwie warmińsko-mazurskim. Do tego program "Za życiem" oraz ośrodki pomocy społecznej,
            czyli miejsca, w których rodzic realnie załatwia sprawy dziecka.
          </p>
        </div>
      </header>

      <div className={`${page.wrap} ${page.wrapWide}`}>
        <Breadcrumbs items={CRUMBS} />

        <section className={page.section}>
          <p>
            Terapia w gabinecie to jedna część wsparcia dziecka. Druga toczy się w instytucjach publicznych: to
            poradnia psychologiczno-pedagogiczna wydaje opinię o wczesnym wspomaganiu rozwoju, opinię o
            dostosowaniu wymagań i orzeczenie o potrzebie kształcenia specjalnego, a ośrodek pomocy społecznej
            lub PCPR uruchamia świadczenia i dofinansowania. Rodzice pytają mnie o te adresy niemal na każdym
            omówieniu diagnozy, dlatego zebrałam je w jednym miejscu.
          </p>
          <p>
            Wszystkie placówki na tej liście działają <strong>bezpłatnie</strong>, a zgłoszenia dokonuje rodzic
            lub opiekun prawny, bez skierowania od lekarza. Dane sprawdziłam na stronach placówek w
            {' '}{AKTUALIZACJA} roku.
          </p>

          <div className={page.callout}>
            <strong>Skrót dla zabieganych:</strong> poradnia publiczna wydaje dokumenty i prowadzi bezpłatne
            zajęcia, gabinet prywatny wykonuje szczegółową diagnozę integracji sensorycznej i terapię, a MOPS
            lub PCPR odpowiada za świadczenia i dofinansowania. Te ścieżki nie konkurują, tylko się uzupełniają.
          </div>

          <ul className={page.chips} style={{ marginTop: '1.4rem' }}>
            {SPIS.map(([href, label]) => (
              <li key={href}>
                <a href={href}>{label}</a>
              </li>
            ))}
          </ul>
        </section>

        <section className={page.section} id="kiedy">
          <h2>Poradnia publiczna czy gabinet integracji sensorycznej</h2>
          <p>
            <strong>Do poradni psychologiczno-pedagogicznej</strong> idziesz wtedy, gdy potrzebujesz dokumentu
            albo bezpłatnego wsparcia systemowego: opinii o potrzebie wczesnego wspomagania rozwoju, orzeczenia
            o potrzebie kształcenia specjalnego, opinii o specyficznych trudnościach w uczeniu się, opinii w
            sprawie odroczenia obowiązku szkolnego, konsultacji psychologa, pedagoga lub logopedy.
          </p>
          <p>
            <strong>Do gabinetu integracji sensorycznej</strong> zgłaszasz się, gdy chcesz zrozumieć, jak
            dziecko odbiera bodźce i co robić na co dzień. Diagnoza SI opisuje układ przedsionkowy,
            propriocepcję, czucie dotyku i planowanie ruchu, a kończy się zaleceniami do domu, przedszkola i
            szkoły. Terminy są zwykle krótsze niż w poradni, a opis można później dołączyć do wniosku.
            Zobacz, jak wygląda{' '}
            <a href="/diagnoza-sensoryczna-olsztyn/">diagnoza integracji sensorycznej w Olsztynie</a> i{' '}
            <a href="/terapia-integracji-sensorycznej-olsztyn/">terapia SI</a>.
          </p>
          <p>
            W praktyce najczęściej łączymy obie ścieżki: rodzic czeka na termin w poradni, a w tym czasie
            wykonuje diagnozę sensoryczną i zaczyna pracę z dzieckiem. Nic się nie dubluje, bo poradnia ocenia
            funkcjonowanie poznawcze i emocjonalne, a diagnoza SI procesy sensomotoryczne.
          </p>
        </section>

        <section className={page.section} id="olsztyn">
          <h2>Poradnie psychologiczno-pedagogiczne w Olsztynie</h2>
          <p>
            W Olsztynie działają dwie miejskie poradnie z podziałem na rejony (decyduje adres przedszkola lub
            szkoły dziecka, a przy dzieciach nieobjętych edukacją adres zamieszkania) oraz poradnia powiatowa
            obsługująca gminy powiatu olsztyńskiego.
          </p>
          <div className={page.cardGrid}>
            {PPP_OLSZTYN.map((p) => (
              <Placowka key={p.nazwa} p={p} />
            ))}
          </div>
          <p className={page.muted} style={{ marginTop: '1.2rem' }}>
            Dawna Poradnia nr 2 przy ul. Jagiellończyka została włączona w strukturę poradni miejskiej, więc
            rodzice kierowani są dziś do poradni nr 1 albo nr 3.
          </p>
        </section>

        <section className={page.section} id="powiat">
          <h2>Powiat olsztyński: punkty konsultacyjne blisko domu</h2>
          <p>
            Powiatowa Poradnia Psychologiczno-Pedagogiczna prowadzi cztery punkty w terenie, dzięki czemu
            rodziny z gmin powiatu nie muszą dojeżdżać do Olsztyna na każde spotkanie.
          </p>
          <div className={`${page.cardGrid} ${page.cardGrid3}`}>
            {PUNKTY_POWIAT.map((p) => (
              <Placowka key={p.nazwa} p={p} compact />
            ))}
          </div>
        </section>

        <section className={page.section} id="za-zyciem">
          <h2>Program "Za życiem": bezpłatne zajęcia dla najmłodszych dzieci</h2>
          <p>
            To ścieżka, o której rodzice wiedzą najmniej, a która potrafi dać dziecku kilka dodatkowych godzin
            terapii tygodniowo, bez opłat. Program obejmuje dzieci z trudnościami rozwojowymi{' '}
            <strong>od urodzenia do rozpoczęcia nauki w szkole</strong> oraz ich rodziny. Nie trzeba czekać na
            pełną diagnozę medyczną, wystarczy jeden z dokumentów potwierdzających niepełnosprawność lub jej
            zagrożenie.
          </p>
          <div className={page.cardGrid}>
            {ZA_ZYCIEM.map((p) => (
              <Placowka key={p.nazwa} p={p} />
            ))}
          </div>
          <div className={page.callout}>
            Jeśli dziecko korzysta z zajęć w ramach programu "Za życiem", terapia SI w gabinecie prywatnym jest
            ich uzupełnieniem, a nie zamiennikiem. Warto, żeby specjaliści z obu miejsc wiedzieli o sobie
            nawzajem i pracowali nad tymi samymi celami.
          </div>
        </section>

        <section className={page.section} id="warmia">
          <h2>Warmia: Braniewo, Lidzbark Warmiński, Bartoszyce, Kętrzyn</h2>
          <p>
            Do gabinetu w Olsztynie przyjeżdżają rodziny z całej Warmii. Poniżej poradnie z powiatów, z których
            trafia do mnie najwięcej dzieci.
          </p>
          <div className={page.cardGrid}>
            {PPP_WARMIA.map((p) => (
              <Placowka key={p.nazwa} p={p} />
            ))}
          </div>
        </section>

        <section className={page.section} id="elblag">
          <h2>Elbląg i powiat elbląski</h2>
          <p>
            W Elblągu działają dwie poradnie miejskie z podziałem na rejony, a gminy powiatu elbląskiego
            obsługuje poradnia w Pasłęku.
          </p>
          <div className={`${page.cardGrid} ${page.cardGrid3}`}>
            {PPP_ELBLAG.map((p) => (
              <Placowka key={p.nazwa} p={p} compact />
            ))}
          </div>
        </section>

        <section className={page.section} id="region">
          <h2>Pozostałe poradnie w województwie warmińsko-mazurskim</h2>
          <p>
            Rejonizacja oznacza, że każde dziecko ma przypisaną konkretną poradnię, ale po informację i wstępną
            rozmowę możesz zadzwonić do każdej z nich.
          </p>
          <div className={`${page.cardGrid} ${page.cardGrid3}`}>
            {PPP_REGION.map((p) => (
              <Placowka key={p.nazwa} p={p} compact />
            ))}
          </div>
        </section>

        <section className={page.section} id="pomoc-spoleczna">
          <h2>MOPS, GOPS, CUS i PCPR: wsparcie, którego nie daje poradnia</h2>
          <p>
            Ośrodek pomocy społecznej i powiatowe centrum pomocy rodzinie zajmują się tym, czego poradnia nie
            prowadzi: świadczeniami dla rodzin dzieci z niepełnosprawnością, dofinansowaniami z PFRON do
            sprzętu, turnusów i likwidacji barier, orzeczeniami o niepełnosprawności, asystentem rodziny oraz
            pomocą w kryzysie. Dla wielu rodzin to właśnie te pieniądze i usługi decydują o tym, czy uda się
            utrzymać regularną terapię.
          </p>
          {POMOC_SPOLECZNA.map((g) => (
            <div key={g.grupa} style={{ marginTop: '1.6rem' }}>
              <h3>{g.grupa}</h3>
              <div className={`${page.cardGrid} ${page.cardGrid3}`}>
                {g.placowki.map((p) => (
                  <Placowka key={p.nazwa} p={p} compact />
                ))}
              </div>
            </div>
          ))}
          <p className={page.muted} style={{ marginTop: '1.4rem' }}>
            Mieszkasz w innej gminie? Szukaj ośrodka pomocy społecznej pod nazwą swojej gminy (GOPS, MGOPS lub
            CUS). Sprawy powiatowe, w tym orzeczenia o niepełnosprawności i wnioski do PFRON, prowadzi PCPR w
            mieście powiatowym.
          </p>
        </section>

        <section className={page.section} id="wizyta">
          <h2>Jak przygotować się do wizyty w poradni</h2>
          <p>Pierwsze spotkanie idzie sprawniej, gdy masz ze sobą komplet informacji o dziecku:</p>
          <ul className={page.list}>
            <li>wypełniony wniosek o badanie (druki są na stronach poradni lub w sekretariacie),</li>
            <li>opinię z przedszkola lub szkoły o funkcjonowaniu dziecka w grupie,</li>
            <li>
              dokumentację medyczną: karty informacyjne, wyniki badań słuchu i wzroku, konsultacje
              neurologiczne lub psychiatryczne,
            </li>
            <li>wcześniejsze opinie i orzeczenia, jeśli dziecko już je ma,</li>
            <li>opis diagnozy integracji sensorycznej albo innej terapii, w której dziecko uczestniczyło,</li>
            <li>zeszyty i prace dziecka, gdy zgłoszenie dotyczy pisania, czytania lub liczenia.</li>
          </ul>
          <p style={{ marginTop: '1.2rem' }}>
            Zapisz też konkretne obserwacje z domu: kiedy trudność się pojawia, jak długo trwa, co pomaga, a co
            ją nasila. Takie notatki są dla diagnosty cenniejsze niż ogólne stwierdzenie, że dziecko jest
            niespokojne.
          </p>
        </section>

        <section className={page.section} id="faq">
          <h2>Najczęstsze pytania rodziców</h2>
          <div className={page.faqList}>
            {PORADNIE_FAQ.map((f, i) => (
              <details className={page.faqItem} key={f.q}>
                <summary className={page.question}>
                  <span className={page.qIndex}>{String(i + 1).padStart(2, '0')}</span>
                  {f.q}
                </summary>
                <div className={page.answer}>
                  <p>{f.a}</p>
                </div>
              </details>
            ))}
          </div>
        </section>

        <p className={page.muted}>
          Dane teleadresowe zebrane na stronach placówek ({AKTUALIZACJA}). Poradnie i
          ośrodki zmieniają siedziby, numery i godziny pracy, dlatego przed wizytą potwierdź szczegóły
          telefonicznie. Treść ma charakter informacyjny i nie zastępuje indywidualnej konsultacji.
        </p>
      </div>

      <section className={page.ctaBand}>
        <div className={page.ctaInner}>
          <h2>Nie wiesz, od której instytucji zacząć?</h2>
          <p>
            Napisz albo zadzwoń. Pomogę ustalić kolejność kroków, nawet jeśli pierwszym z nich nie będzie
            wizyta u mnie, tylko wniosek w poradni rejonowej.
          </p>
          <div className={page.ctaBtns}>
            <a href={`tel:${FIRMA.telefonE164}`} className={page.btnPrimary}>
              Zadzwoń: {FIRMA.telefon}
            </a>
            <a href={`mailto:${FIRMA.email}`} className={page.btnGhost}>
              Napisz wiadomość
            </a>
            <a href="/diagnoza-sensoryczna-olsztyn/" className={page.btnGhost}>
              Diagnoza sensoryczna Olsztyn
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
